"use client";

import React, { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { conventionneTripFormSchema } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { createConventionneAttempt } from "@/lib/actions/conventionne.actions";
import MyCustomFormField, { FormFieldType } from "./MyCustomFormField";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Card, CardContent } from "./ui/card";
import AutoComplete from "./AutoComplete";
import Link from "next/link";

const isNotEmptyAndhasNoDigits = (str: string | undefined) => {
  if (!str) return false;
  for (let i = 0; i < str.length; i++) {
    if (str[i] >= "0" && str[i] <= "9") {
      return false;
    }
  }
  return true;
};

// https://www.youtube.com/watch?v=6wo9xKOZCcA

const ConventionneCreateTripForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTab, setSelectedTab] = useState("immediate");
  const [selectecSchedule, setSelectecSchedule] = useState<Date | null>(null);

  //new fields
  const [departure, setDeparture] = useState<FullAddress>({});
  const [destination, setDestination] = useState<FullAddress>({});

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  const onTabChange = (value: string) => {
    setSelectedTab(value);
    setError("");
  };

  const router = useRouter();

  const formSchema = conventionneTripFormSchema();

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      schedule: undefined,
      name: "",
      passenger_telephone: "",
      message_for_taxi: "",
    },
  });

  // 2. Define a submit handler.
  const onSubmit = async (form_data: z.infer<typeof formSchema>) => {
    setError("");
    //
    // departureFormattedAddress
    let departure_can_be_share = false;
    let departureFormattedAddress = `${departure.street_number} ${departure.route}`.trim();
    if (isNotEmptyAndhasNoDigits(departure.name)) {
      departure_can_be_share = true;
      departureFormattedAddress =
        `${departure.name} ${departure.street_number} ${departure.route}`.trim();
    }
    // departureFormattedAddress
    let departureFullAddress =
      `${departure.street_number} ${departure.route} ${departure.postal_code} ${departure.locality}`.trim();
    if (isNotEmptyAndhasNoDigits(departure.name)) {
      departureFullAddress =
        `${departure.name} ${departure.street_number} ${departure.route} ${departure.postal_code} ${departure.locality}`.trim();
    }

    // destinationFormattedAddress
    let destinationFormattedAddress =
      `${destination.postal_code} ${departure.locality}`.trim();
    if (isNotEmptyAndhasNoDigits(destination.name)) {
      destinationFormattedAddress =
        `${destination.name} ${destination.postal_code} ${destination.locality}`.trim();
    }
    // destinationFullAddress
    let destinationFullAddress =
      `${destination.street_number} ${destination.route} ${destination.postal_code} ${destination.locality}`.trim();
    if (isNotEmptyAndhasNoDigits(destination.name)) {
      destinationFullAddress =
        `${destination.name} ${destination.street_number} ${destination.route} ${destination.postal_code} ${destination.locality}`.trim();
    }

    //check for errors - departure & destination
    if (!departure.postal_code || !departure.locality) {
      setError("Veuillez choisir une autre adresse de départ");
      return;
    }
    if (!departureFormattedAddress) {
      setError("Il faut indiquer l'adresse de départ");
      return;
    }
    if (!destinationFormattedAddress) {
      setError("Il faut indiquer l'adresse de destination");
      return;
    }
    if (!destination.postal_code || !destination.locality) {
      setError("Veuillez sélectionner une autre adresse proposée pour la destination");
      return;
    }
    //
    if (!form_data.accept_cgu) {
      setError("Vous devez accepter les CGU et la politique sur les données privées");
      return;
    }
    //check for errors - schedule
    if (selectedTab === "advance") {
      if (!form_data.schedule) {
        setError(
          "Il faut indiquer la date et l'heure pour les réservations à l'avance"
        );
        return;
      }
      if (form_data.schedule < new Date(Date.now() + 2 * (60 * 60 * 1000))) {
        setError("La réservation doit être dans au moins 2 heures");
        return;
      }
    }
    //
    setIsLoading(true);
    console.log("onSubmit", form_data);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    try {
      const sent_data = {
        //destination
        schedule:
          selectedTab === "immediate"
            ? ""
            : form_data.schedule?.toISOString() || "",
        fullSchedule:
          selectedTab === "immediate"
            ? ""
            : form_data.schedule?.toLocaleString("fr-Fr") || "",
        //
        message_for_taxi: form_data.message_for_taxi!,
        // passenger
        passenger_name: form_data.name,
        social_security_number: form_data.social_security_number,
        passenger_telephone: form_data.passenger_telephone,
        // departure
        departure_can_be_share: departure_can_be_share,
        departureFullAddress: departureFullAddress,
        departure_address: departureFormattedAddress,
        departure_zipcode: departure.postal_code,
        departure_city_name: departure.locality,
        departure_latitude: departure.latitude,
        departure_longitude: departure.longitude,
        //destination
        destinationFullAddress: destinationFullAddress,
        destination_address: destinationFormattedAddress,
        destination_latitude: destination.latitude,
        destination_longitude: destination.longitude,
        // optional data
        departure_street_number: departure.street_number,
        departure_route: departure.route,
        departure_administrative_area_level_2:
          departure.administrative_area_level_2,
        departure_administrative_area_level_1:
          departure.administrative_area_level_1,
        departure_postal_code: departure.postal_code,
        departure_locality: departure.locality,
        departure_name: departure.name,
        //
        destination_street_number: destination.street_number,
        destination_route: destination.route,
        destination_postal_code: destination.postal_code,
        destination_administrative_area_level_2:
          destination.administrative_area_level_2,
        destination_administrative_area_level_1:
          destination.administrative_area_level_1,
        destination_locality: destination.locality,
        destination_name: destination.name,
      };

      const { data, error } =
        (await createConventionneAttempt(sent_data)) || {};
      if (error) {
        toast.error(error);
        await delay(2000);
      } else {
        toast.success("Merci de valider votre commande");
        await delay(2000);
      }
    } catch (err) {
      toast.error("Une erreur s'est produite lors de la commande");
      await delay(2000);
      console.log(err);
    }

    setIsLoading(false);
    // clean
    //form.reset();
    //setSelectecSchedule(null);
    router.refresh();
  };

  return (
    <section className="flex flex-col justify-center bg-red w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-2"
          autoComplete="off"
        >
          <div className="flex  gap-4 w-full items-center justify-between">
            <h2 className="header-2">Commander un taxi conventionné</h2>
          </div>

          <div className="flex flex-col">
            <p className="text-sm font-medium mb-2">Adresse de départ</p>
            <AutoComplete
              setData={setDeparture}
              placeholder={"Entrer votre adresse de départ"}
            />
            {/* <p>{JSON.stringify(departure)}</p> */}
          </div>

          <div className="flex flex-col">
            <p className="text-sm font-medium mb-2">Adresse de destination</p>
            <AutoComplete
              setData={setDestination}
              placeholder={"Entrer votre adresse de destination"}
            />
            {/* <p>{JSON.stringify(destination)}</p> */}
          </div>
          <Tabs
            defaultValue={selectedTab}
            className="w-full"
            onValueChange={onTabChange}
          >
            <TabsList className="grid w-full grid-cols-2 bg-slate-200">
              <TabsTrigger
                value="immediate"
                className="data-[state=active]:bg-white"
              >
                Immédiat
              </TabsTrigger>
              <TabsTrigger
                value="advance"
                className="data-[state=active]:bg-white"
              >
                A l&rsquo;avance
              </TabsTrigger>
            </TabsList>
            <TabsContent value="immediate"></TabsContent>
            <TabsContent value="advance">
              <Card>
                <CardContent className="p-0">
                  <MyCustomFormField
                    showTimeSelect={true}
                    fieldType={FormFieldType.DATETIME_PICKER}
                    control={form.control}
                    name="schedule"
                    label=""
                    placeholder="Sélectionner la date"
                    onChangeDate={(date) => {
                      setSelectecSchedule(date);
                    }}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex items-center">
            <p className="text-sm font-medium mr-4">Votre nom</p>
            <MyCustomFormField
              fieldType={FormFieldType.INPUT}
              control={form.control}
              name="name"
              label=""
              placeholder="Antoine Dupont"
            ></MyCustomFormField>
          </div>

          <div className="flex items-center">
            <p className="text-sm font-medium mr-4">
              Votre numéro de sécurité sociale
            </p>
            <MyCustomFormField
              fieldType={FormFieldType.INPUT}
              control={form.control}
              name="social_security_number"
              label=""
              placeholder="123456789012345"
            ></MyCustomFormField>
          </div>
          <div className="flex items-center">
            <p className="text-sm font-medium mr-4">
              Votre numéro de téléphone
            </p>
            <MyCustomFormField
              fieldType={FormFieldType.INPUT}
              control={form.control}
              name="passenger_telephone"
              label=""
              placeholder="0601020304"
            ></MyCustomFormField>
          </div>
          <MyCustomFormField
            fieldType={FormFieldType.INPUT}
            control={form.control}
            name="message_for_taxi"
            label="Message pour le taxi"
            placeholder="Remboursement, bon de transport"
            maxLength={20}
          ></MyCustomFormField>
          <div className="flex justify-start items-center ali">
            <div>
              <MyCustomFormField
                fieldType={FormFieldType.CHECKBOX}
                control={form.control}
                name="accept_cgu"
                label=""
                placeholder=""
                maxLength={20}
              ></MyCustomFormField>
            </div>
            <div className="text-sm font-medium mr-4">
              J&apos;accepte les{" "}<u>
              <Link 
                href={"https://www.taxisconnect.com/a3taxi/profile/contract/"}
              >
                CGU
              </Link></u>{" "}
              et la{" "}<u>
              <Link href={"https://www.taxisconnect.com/taxi_app/"}>
                politique sur les données privées
              </Link></u>
            </div>
          </div>
          {error && (
            <div className="text-red-500 text-sm font-medium">{error}</div>
          )}
          <div className="flex flex-col gap-4">
            <Button type="submit" className="form-btn" disabled={isLoading}>
              {isLoading ? (
                <div className="flex">
                  <Loader2 size={20} className="animate-spin" /> &nbsp;
                  Attendre...
                </div>
              ) : selectedTab === "immediate" ? (
                "Commander"
              ) : (
                "Commander à l'avance"
              )}
            </Button>
          </div>
        </form>
      </Form>
      <footer className="flex justify-center gap-1"> </footer>
    </section>
  );
};

export default ConventionneCreateTripForm;
